// ── Configuration ──────────────────────────────────────────────────────────

export interface LifecycleConfig {
  /** Idle timeout before the agent stops (default: 30 min) */
  idleTimeoutMs: number;
  /** Dormant timeout — bound on dormant-after-completed wait (default: 60 min).
   *  Defense-in-depth: the server-side polling is supposed to shut us down,
   *  but if it regresses, the agent shuts itself down here. */
  dormantTimeoutMs: number;
  /** Heartbeat interval (default: 30s) */
  heartbeatIntervalMs: number;
  /** Token refresh interval (default: 45 min — before 1h GitHub token expiry) */
  tokenRefreshIntervalMs: number;
  /** Periodic WIP git flush interval (default: 2 min). Covers ungraceful
   *  pod termination (OOMKilled, node eviction) where preStop doesn't run.
   *  Set to `0` to disable the timer entirely — used for environments with
   *  persistent state (local, workspace, GitHub Codespaces) that don't lose
   *  in-flight work on a crash. */
  gitFlushIntervalMs: number;
  /** Claude key usage sample interval (default: 5 min). Polls the Anthropic
   *  OAuth usage endpoint and reports session/weekly rate-limit utilization for
   *  the running subscription key. Deliberately NOT the heartbeat cadence — the
   *  endpoint is rate-limited. Set to `0` to disable the timer entirely. */
  usageSampleIntervalMs: number;
  /** Delay before the FIRST usage sample (default: 30s). The sample spawns a
   *  full `claude -p "/usage"` subprocess; sampling immediately at startup put
   *  that CPU cost inside the boot-critical window on the throttled controller
   *  container. Usage freshness 30s later costs nothing. */
  usageSampleInitialDelayMs: number;
}

export interface LifecycleCallbacks {
  onHeartbeat: () => void;
  onIdleTimeout: () => void;
  onDormantTimeout: () => void;
  onTokenRefresh: () => void;
  onGitFlush: () => void;
  onUsageSample: () => void;
}

// ── Default configuration ──────────────────────────────────────────────────

/** 30 min idle, 60 min dormant, 30s heartbeat, 45 min token refresh, 2 min git flush, 5 min usage sample */
export const DEFAULT_LIFECYCLE_CONFIG: LifecycleConfig = {
  idleTimeoutMs: 30 * 60 * 1000,
  dormantTimeoutMs: 60 * 60 * 1000,
  heartbeatIntervalMs: 30_000,
  tokenRefreshIntervalMs: 45 * 60 * 1000,
  gitFlushIntervalMs: 2 * 60 * 1000,
  usageSampleIntervalMs: 5 * 60 * 1000,
  usageSampleInitialDelayMs: 30_000,
};

// ── Lifecycle: timer management for idle/heartbeat ───────────────────────

export class Lifecycle {
  readonly config: LifecycleConfig;
  private readonly callbacks: LifecycleCallbacks;

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private idleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private dormantTimer: ReturnType<typeof setTimeout> | null = null;
  private gitFlushTimer: ReturnType<typeof setInterval> | null = null;
  private usageSampleTimer: ReturnType<typeof setInterval> | null = null;

  constructor(config: LifecycleConfig, callbacks: LifecycleCallbacks) {
    this.config = config;
    this.callbacks = callbacks;
  }

  // ── Heartbeat ──────────────────────────────────────────────────────

  startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.callbacks.onHeartbeat();
    }, this.config.heartbeatIntervalMs);
  }

  stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ── Token refresh ─────────────────────────────────────────────────

  startTokenRefresh(): void {
    this.stopTokenRefresh();
    // Immediately refresh to ensure we have a fresh token at startup
    this.callbacks.onTokenRefresh();
    this.tokenRefreshTimer = setInterval(() => {
      this.callbacks.onTokenRefresh();
    }, this.config.tokenRefreshIntervalMs);
  }

  stopTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  // ── Periodic git flush ────────────────────────────────────────────

  startGitFlush(): void {
    this.stopGitFlush();
    if (this.config.gitFlushIntervalMs <= 0) return;
    this.gitFlushTimer = setInterval(() => {
      this.callbacks.onGitFlush();
    }, this.config.gitFlushIntervalMs);
  }

  stopGitFlush(): void {
    if (this.gitFlushTimer) {
      clearInterval(this.gitFlushTimer);
      this.gitFlushTimer = null;
    }
  }

  // ── Claude key usage sampling ─────────────────────────────────────

  startUsageSample(): void {
    this.stopUsageSample();
    if (this.config.usageSampleIntervalMs <= 0) return;
    // First sample after a short delay — NOT immediately: the sample spawns a
    // `claude -p "/usage"` subprocess, and doing that at startup raced the
    // boot-critical path for CPU. Then on the interval.
    this.usageSampleTimer = setTimeout(() => {
      this.callbacks.onUsageSample();
      this.usageSampleTimer = setInterval(() => {
        this.callbacks.onUsageSample();
      }, this.config.usageSampleIntervalMs);
    }, this.config.usageSampleInitialDelayMs);
  }

  stopUsageSample(): void {
    if (this.usageSampleTimer) {
      clearInterval(this.usageSampleTimer);
      this.usageSampleTimer = null;
    }
  }

  // ── Idle timer ─────────────────────────────────────────────────────

  startIdleTimer(): void {
    this.clearIdleTimers();
    this.idleTimer = setTimeout(() => {
      this.callbacks.onIdleTimeout();
    }, this.config.idleTimeoutMs);
  }

  cancelIdleTimer(): void {
    this.clearIdleTimers();
  }

  // ── Dormant timer ──────────────────────────────────────────────────

  /** Start (or restart) the dormant timer.
   *  @param overrideMs Optional custom delay in ms. When provided, the timer
   *  fires after exactly that delay instead of `dormantTimeoutMs`. SessionRunner
   *  uses this to enforce an *absolute* deadline across cycles: even if the
   *  dormant wait is interrupted by an inbound message, the next iteration
   *  passes the remaining time, so the agent shuts down at the original
   *  deadline regardless of message volume. */
  startDormantTimer(overrideMs?: number): void {
    this.cancelDormantTimer();
    const delay = Math.max(0, overrideMs ?? this.config.dormantTimeoutMs);
    this.dormantTimer = setTimeout(() => {
      this.callbacks.onDormantTimeout();
    }, delay);
  }

  cancelDormantTimer(): void {
    if (this.dormantTimer) {
      clearTimeout(this.dormantTimer);
      this.dormantTimer = null;
    }
  }

  // ── Cleanup ────────────────────────────────────────────────────────

  destroy(): void {
    this.stopHeartbeat();
    this.stopTokenRefresh();
    this.stopGitFlush();
    this.stopUsageSample();
    this.clearIdleTimers();
    this.cancelDormantTimer();
  }

  // ── Private ────────────────────────────────────────────────────────

  private clearIdleTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
  }
}
